import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Radio,
  Upload,
  message,
} from 'antd';
import {
  BarChartOutlined,
  CopyOutlined,
  DatabaseOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ExperimentOutlined,
  FileExcelOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  StopOutlined,
  UploadOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import Plotly from 'plotly.js-dist-min';
import createPlotlyComponent from 'react-plotly.js/factory';
import { adminAPI } from '../services/api';
import { getDrugLabel } from '../constants/drugs';
import { getPopulationLabel, POPULATION_TAG_OPTIONS } from '../constants/populations';
import { formatErrorMessage } from '../utils/errorHandler';
import SyntheticValidationDatasetModal from './SyntheticValidationDatasetModal';
import {
  CompactStatusGlyph,
  CompactStatusLegend,
  resolveCompactStatusTone,
} from './CompactStatus';
import {
  getCovariateTagStyle,
  getDrugTagStyle,
  getPopulationTagColor,
} from '../utils/clinicalTagPalette';
import './ModelValidationCenter.css';

export { filterValidationModels } from './ModelScriptHealthPanel';

const Plot = createPlotlyComponent(Plotly);

export const resolveValidationResultResponse = (
  requestId: number,
  currentRequestId: number,
  previousModelId: string | undefined,
  summaries: Array<{ model_id?: string }>,
) => {
  if (requestId !== currentRequestId) {
    return { apply: false, selectedModelId: previousModelId };
  }
  const selectedModelId = previousModelId
    && summaries.some((summary) => summary.model_id === previousModelId)
    ? previousModelId
    : summaries[0]?.model_id;
  return { apply: true, selectedModelId };
};

const isRetryableValidationCenterError = (error: unknown): boolean => (
  typeof error === 'object'
  && error !== null
  && 'code' in error
  && (error as { code?: unknown }).code === 'ERR_NETWORK'
);

export const retryValidationCenterRequest = async <T,>(
  request: () => Promise<T>,
): Promise<T> => {
  try {
    return await request();
  } catch (error) {
    if (!isRetryableValidationCenterError(error)) throw error;
    return request();
  }
};

export const settleValidationCenterRequests = async <
  T extends Record<string, () => Promise<unknown>>,
>(requests: T): Promise<{
  values: Partial<{ [K in keyof T]: Awaited<ReturnType<T[K]>> }>;
  errors: Array<{ key: keyof T; reason: unknown }>;
}> => {
  const keys = Object.keys(requests) as Array<keyof T>;
  const settled = await Promise.allSettled(
    keys.map((key) => retryValidationCenterRequest(requests[key])),
  );
  const values = {} as Partial<{ [K in keyof T]: Awaited<ReturnType<T[K]>> }>;
  const errors: Array<{ key: keyof T; reason: unknown }> = [];

  settled.forEach((result, index) => {
    const key = keys[index];
    if (result.status === 'fulfilled') {
      values[key] = result.value as Awaited<ReturnType<T[typeof key]>>;
    } else {
      errors.push({ key, reason: result.reason });
    }
  });

  return { values, errors };
};
const { Paragraph, Text, Title } = Typography;

type ValidationStatus = 'passed' | 'warning' | 'failed' | 'running' | 'completed' | string;

export interface ValidationModel {
  model_id: string;
  display_name?: string;
  drug?: string;
  drug_name?: string;
  drug_label?: string;
  inferred_drug_name?: string;
  script_file_base?: string;
  first_author?: string;
  publication_year?: number;
  model_status?: string;
  qualification_status?: string;
  review_status?: string;
  script_registration_status?: string;
  model_units?: {
    dose_amount?: string;
    concentration?: string;
    auc?: string;
    time?: string;
  };
}

interface ScriptReport {
  id: string;
  model_id: string;
  script_file_base?: string;
  status: ValidationStatus;
  checks?: Record<string, unknown>;
  required_covariates?: string[];
  warnings?: unknown;
  errors?: unknown;
  duration_ms?: number;
  created_at?: string;
}

interface ValidationDataset {
  id: string;
  name: string;
  drug_name: string;
  description?: string;
  source_type?: string;
  status: ValidationStatus;
  counts?: Record<string, number>;
  covariate_keys?: string[];
  quality_report?: Record<string, unknown>;
  import_summary?: Record<string, unknown>;
  created_at?: string;
}

interface ValidationRun {
  id: string;
  dataset_id: string;
  name?: string;
  status: ValidationStatus;
  mode: string;
  model_ids: string[];
  settings?: Record<string, number>;
  progress?: Record<string, number>;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
  created_at?: string;
}

interface ValidationSummary {
  id: string;
  model_id: string;
  attempted_predictions?: number;
  failed_predictions?: number;
  missing_covariates?: string[];
  top_errors?: Array<{ message: string; count: number }>;
  n_predictions: number;
  n_subjects: number;
  mpe?: number;
  mpe_percent?: number;
  mae?: number;
  mape?: number;
  rmse?: number;
  f20?: number;
  f30?: number;
  map_success_rate?: number;
  residual_diagnostics?: Record<string, unknown>;
  simulation_diagnostics?: Record<string, unknown>;
  overall_status: ValidationStatus;
  conclusion?: string;
}

interface ValidationPrediction {
  id: string;
  model_id: string;
  prediction_type: string;
  training_observation_count: number;
  observed_concentration: number;
  predicted_concentration?: number;
  residual?: number;
  percent_error?: number;
  weighted_residual?: number;
  map_success?: boolean;
  prediction_interval?: Record<string, number | string | null>;
  error_message?: string;
}

interface RunResults {
  run: ValidationRun;
  summaries: ValidationSummary[];
  predictions: ValidationPrediction[];
}

interface SampleDataset {
  key: string;
  name: string;
  drug_name: string;
  description?: string;
  counts?: Record<string, number>;
}

interface ClinicalCandidate {
  calculation_id: string;
  dedupe_key?: string;
  duplicate_count?: number;
  duplicate_calculation_ids?: string[];
  case_id: string;
  patient_code: string;
  drug_name: string;
  model_id?: string;
  calculation_type?: string;
  population_tags?: string[];
  tdm_count: number;
  dosing_event_count?: number;
  tdm_summary?: string;
  created_at?: string;
  diagnosis?: string;
}

const DATASET_EXAMPLE = {
  subjects: [
    {
      subject_key: 'P001',
      covariates: { WT: 12, AGE: 3, SCR: 25 },
      strata: { ward: 'PICU' },
    },
  ],
  dosing_events: [
    {
      subject_key: 'P001',
      event_time_h: 0,
      dose_mg: 180,
      infusion_h: 1,
      interval_h: 8,
      route: 'iv',
    },
  ],
  observations: [
    {
      subject_key: 'P001',
      sample_time_h: 7.5,
      concentration: 10.2,
      unit: 'mg/L',
      sample_label: 'trough',
    },
  ],
};

function statusTag(status?: ValidationStatus, compact = false, label = '状态') {
  const normalized = status || 'unknown';
  if (compact) {
    return <CompactStatusGlyph label={label} value={normalized} tone={resolveCompactStatusTone(normalized)} />;
  }
  const color = normalized === 'passed' || normalized === 'completed'
    ? 'success'
    : normalized === 'failed'
      ? 'error'
      : normalized === 'running'
        ? 'processing'
        : 'warning';
  return <Tag color={color}>{normalized}</Tag>;
}

function num(value?: number | null, digits = 2) {
  if (value === undefined || value === null || Number.isNaN(value)) return '-';
  return Number(value).toFixed(digits);
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function safeArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item) => item !== null && item !== undefined).map((item) => {
      const text = typeof item === 'string' ? item : JSON.stringify(item);
      return text || String(item);
    });
  }
  if (value === null || value === undefined || value === '') return [];
  if (typeof value === 'string') return [value];
  return [JSON.stringify(value)];
}

function getPredictionSeries(predictions: ValidationPrediction[], modelId?: string) {
  return modelId ? predictions.filter((item) => item.model_id === modelId) : predictions;
}

function modelDrugName(model: ValidationModel) {
  return model.drug_name || model.drug || model.inferred_drug_name || '';
}

function modelDrugLabel(model: ValidationModel) {
  const value = modelDrugName(model);
  return model.drug_label || (value ? getDrugLabel(value) : '');
}

function normalizedOptionKey(value: unknown) {
  return String(value || '').trim().toLowerCase();
}

function normalizedDrugMatchKey(value: unknown) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return normalizedOptionKey(getDrugLabel(raw, raw));
}

const COVARIATE_ALIASES: Record<string, string[]> = {
  WT: ['wt', 'weight', 'body_weight'],
  SCR: ['scr', 'cr', 'creatinine', 'serum_creatinine'],
  CR: ['cr', 'scr', 'creatinine', 'serum_creatinine'],
  PMA: ['pma', 'age_pma', 'postmenstrual_age'],
  PNA: ['pna', 'age_pna', 'postnatal_age'],
  GA: ['ga', 'gestational_age'],
  AGE: ['age', 'age_years'],
  SEX: ['sex', 'gender'],
  GENDER: ['gender', 'sex'],
  CR_ASSAY: ['cr_assay', 'scr_assay', 'cr_assay_method', 'scr_assay_method'],
};

function normalizeCovariateKey(value: unknown) {
  return String(value || '').trim().toLowerCase();
}

function covariateCandidates(value: string) {
  const raw = String(value || '').trim();
  if (!raw) return [];
  const alias = COVARIATE_ALIASES[raw.toUpperCase()] || [];
  return Array.from(new Set([raw, ...alias].map(normalizeCovariateKey).filter(Boolean)));
}

function missingCovariates(required: string[] | undefined, available: string[] | undefined) {
  const availableSet = new Set(asArray<string>(available).map(normalizeCovariateKey));
  return asArray<string>(required).filter((key) => !covariateCandidates(key).some((candidate) => availableSet.has(candidate)));
}

function renderTagList(items: string[] | undefined, emptyText = '-', kind: 'covariate' | 'neutral' = 'neutral') {
  const values = asArray<string>(items);
  if (!values.length) return <Text type="secondary">{emptyText}</Text>;
  return (
    <Space size={[4, 4]} wrap>
      {values.map((item) => (
        <Tag
          key={item}
          className={kind === 'covariate' ? 'clinical-entity-tag' : undefined}
          style={kind === 'covariate' ? getCovariateTagStyle(item) : undefined}
        >
          {item}
        </Tag>
      ))}
    </Space>
  );
}

const compactPagination = (unit: string) => ({
  showTotal: (total: number) => (
    <Space size={16} wrap>
      <CompactStatusLegend />
      <Text type="secondary">共 {total} {unit}</Text>
    </Space>
  ),
});

type StructuredValue = string | number | boolean | null | undefined;

function formatStructuredValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === '') return <Text type="secondary">-</Text>;
  if (typeof value === 'boolean') {
    return value ? <Tag color="success">是</Tag> : <Tag color="default">否</Tag>;
  }
  if (Array.isArray(value)) {
    return value.length ? value.map((item) => String(item)).join(', ') : <Text type="secondary">-</Text>;
  }
  return String(value);
}

function flattenRecord(value: unknown, prefix = ''): Record<string, StructuredValue> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.entries(value as Record<string, unknown>).reduce<Record<string, StructuredValue>>((acc, [key, item]) => {
    const nextKey = prefix ? `${prefix}.${key}` : key;
    if (item && typeof item === 'object' && !Array.isArray(item)) {
      Object.assign(acc, flattenRecord(item, nextKey));
    } else if (Array.isArray(item)) {
      acc[nextKey] = item.map((entry) => String(entry)).join(', ');
    } else {
      acc[nextKey] = item as StructuredValue;
    }
    return acc;
  }, {});
}

function collectFieldKeys(records: Array<Record<string, unknown>>, field: string, excluded = new Set<string>()) {
  const keys = new Set<string>();
  records.forEach((record) => {
    Object.keys(flattenRecord(record[field])).forEach((key) => {
      if (!excluded.has(key)) keys.add(key);
    });
  });
  return Array.from(keys);
}

function attachFlatFields(
  records: Array<Record<string, unknown>>,
  field: string,
  prefix: string,
  excluded = new Set<string>(),
) {
  return records.map((record) => {
    const next = { ...record };
    Object.entries(flattenRecord(record[field])).forEach(([key, value]) => {
      if (!excluded.has(key)) next[`${prefix}${key}`] = value;
    });
    return next;
  });
}

function dynamicFieldColumns(keys: string[], prefix: string, label: string) {
  return keys.map((key) => ({
    title: `${label}:${key}`,
    dataIndex: `${prefix}${key}`,
    width: 140,
    render: formatStructuredValue,
  }));
}

function buildQualityRows(report: unknown) {
  const quality = (report && typeof report === 'object' ? report : {}) as Record<string, unknown>;
  const rows: Array<Record<string, unknown>> = [];
  const checks = (quality.checks && typeof quality.checks === 'object' ? quality.checks : {}) as Record<string, unknown>;
  Object.entries(checks).forEach(([key, value]) => {
    rows.push({
      id: `check-${key}`,
      item: key,
      result: value,
      status: 'passed',
      detail: '通过',
    });
  });
  safeArray(quality.warnings).forEach((warning, index) => {
    rows.push({
      id: `warning-${index}`,
      item: `warning_${index + 1}`,
      result: '需要处理',
      status: 'warning',
      detail: warning,
    });
  });
  if (!rows.length) {
    rows.push({
      id: 'overall',
      item: 'overall',
      result: quality.status || 'unknown',
      status: quality.status || 'warning',
      detail: '暂无详细检查项',
    });
  }
  return rows;
}

function compactJson(value: unknown) {
  if (!value || (typeof value === 'object' && Object.keys(value as Record<string, unknown>).length === 0)) {
    return '-';
  }
  return JSON.stringify(value);
}

function renderJsonCell(value: unknown) {
  const text = compactJson(value);
  if (text === '-') return <Text type="secondary">-</Text>;
  return (
    <Paragraph
      code
      copyable
      ellipsis={{ rows: 2, tooltip: text }}
      style={{ marginBottom: 0, maxWidth: 360 }}
    >
      {text}
    </Paragraph>
  );
}

interface ModelValidationCenterProps {
  initialModelId?: string;
  onInitialModelConsumed?: () => void;
}

const ModelValidationCenter: React.FC<ModelValidationCenterProps> = ({
  initialModelId,
  onInitialModelConsumed,
}) => {
  const [models, setModels] = useState<ValidationModel[]>([]);
  const [reports, setReports] = useState<ScriptReport[]>([]);
  const [datasets, setDatasets] = useState<ValidationDataset[]>([]);
  const [runs, setRuns] = useState<ValidationRun[]>([]);
  const [sampleDatasets, setSampleDatasets] = useState<SampleDataset[]>([]);
  const [drugOptions, setDrugOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [populationTagOptions, setPopulationTagOptions] = useState(POPULATION_TAG_OPTIONS);
  const [results, setResults] = useState<RunResults | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string>();
  const [selectedResultModel, setSelectedResultModel] = useState<string>();
  const [activeValidationTab, setActiveValidationTab] = useState('datasets');
  const [loading, setLoading] = useState(false);
  const [runLoading, setRunLoading] = useState(false);
  const [datasetModalOpen, setDatasetModalOpen] = useState(false);
  const [syntheticModalOpen, setSyntheticModalOpen] = useState(false);
  const [datasetImportMode, setDatasetImportMode] = useState<'upload' | 'json'>('upload');
  const [datasetFile, setDatasetFile] = useState<File | null>(null);
  const [clinicalModalOpen, setClinicalModalOpen] = useState(false);
  const [clinicalCandidates, setClinicalCandidates] = useState<ClinicalCandidate[]>([]);
  const [clinicalLoading, setClinicalLoading] = useState(false);
  const [selectedClinicalIds, setSelectedClinicalIds] = useState<string[]>([]);
  const [runModalOpen, setRunModalOpen] = useState(false);
  const [datasetDrawer, setDatasetDrawer] = useState<Record<string, unknown> | null>(null);
  const [datasetJson, setDatasetJson] = useState(JSON.stringify(DATASET_EXAMPLE, null, 2));
  const [datasetForm] = Form.useForm();
  const [clinicalForm] = Form.useForm();
  const [runForm] = Form.useForm();
  const resultRequestRef = useRef(0);
  const consumedInitialModelRef = useRef<string>();
  const selectedRunDatasetId = Form.useWatch('dataset_id', runForm);

  useEffect(() => {
    if (!initialModelId || consumedInitialModelRef.current === initialModelId) return;
    consumedInitialModelRef.current = initialModelId;
    runForm.setFieldsValue({ model_ids: [initialModelId] });
    setActiveValidationTab('runs');
    setRunModalOpen(true);
    onInitialModelConsumed?.();
  }, [initialModelId, onInitialModelConsumed, runForm]);

  const reportByModel = useMemo(() => {
    const map = new Map<string, ScriptReport>();
    reports.forEach((report) => {
      if (!map.has(report.model_id)) map.set(report.model_id, report);
    });
    return map;
  }, [reports]);

  const selectedPredictions = useMemo(
    () => getPredictionSeries(asArray<ValidationPrediction>(results?.predictions), selectedResultModel),
    [results, selectedResultModel],
  );
  const selectedRun = useMemo(
    () => runs.find((run) => run.id === selectedRunId),
    [runs, selectedRunId],
  );

  const loadAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { values, errors } = await settleValidationCenterRequests({
        models: () => adminAPI.listModelsMeta(),
        reports: () => adminAPI.listValidationScriptReports({ latest_only: true }),
        datasets: () => adminAPI.listValidationDatasets(),
        runs: () => adminAPI.listValidationRuns(),
        samples: () => adminAPI.listValidationSampleDatasets(),
        drugs: () => adminAPI.listDrugParams(),
      });
      const nextModels = values.models
        ? asArray<ValidationModel>(values.models.models).map((model: ValidationModel) => ({
          ...model,
          drug_name: model.drug_name || model.drug || '',
        }))
        : undefined;
      if (nextModels) setModels(nextModels);
      if (values.reports) setReports(asArray<ScriptReport>(values.reports.items));
      if (values.datasets) setDatasets(asArray<ValidationDataset>(values.datasets.items));
      if (values.samples) setSampleDatasets(asArray<SampleDataset>(values.samples.items));

      const nextRuns = values.runs
        ? asArray<ValidationRun>(values.runs.items)
        : undefined;
      if (nextRuns) {
        setRuns(nextRuns);
        if (!selectedRunId && nextRuns[0]?.id) {
          setSelectedRunId(nextRuns[0].id);
        }
      }

      if (values.drugs || nextModels) {
        const drugRes = values.drugs || {};
        const drugRows = asArray<any>(drugRes.items || drugRes.drugs || drugRes);
        const nextDrugOptions: Array<{ value: string; label: string }> = [];
        const seenDrugValues = new Set<string>();
        const seenDrugLabels = new Set<string>();
        const addDrugOption = (rawValue: unknown, rawLabel?: unknown) => {
          const value = String(rawValue || '').trim();
          if (!value) return;
          const label = String(rawLabel || getDrugLabel(value) || value).trim();
          const valueKey = normalizedOptionKey(value);
          const labelKey = normalizedOptionKey(label);
          if (seenDrugValues.has(valueKey) || seenDrugLabels.has(labelKey)) return;
          seenDrugValues.add(valueKey);
          seenDrugLabels.add(labelKey);
          nextDrugOptions.push({ value, label });
        };
        drugRows.forEach((drug: any) => {
          const value = drug.drug_id || drug.drug_name || drug.name;
          addDrugOption(value, drug.display_name || drug.drug_name || drug.name);
        });
        setDrugOptions(nextDrugOptions);
      }

      if (errors.length && !silent) {
        const labels: Record<string, string> = {
          models: '模型',
          reports: '脚本报告',
          datasets: '临床数据集',
          runs: '验证任务',
          samples: '示例数据集',
          drugs: '药物配置',
        };
        message.warning(`部分数据加载失败：${errors.map(({ key }) => labels[String(key)] || String(key)).join('、')}`);
      }
      adminAPI.getPopulationTags()
        .then((res) => {
          const dynamicTags = asArray<string>(res.tags).map((tag) => ({
            value: tag,
            label: getPopulationLabel(tag),
          }));
          if (dynamicTags.length) setPopulationTagOptions(dynamicTags);
        })
        .catch(() => undefined);
    } catch (error) {
      message.error(formatErrorMessage(error, '加载临床证据中心失败'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedRunId, selectedRun?.status, selectedRun?.completed_at]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    const hasActiveRun = runs.some((run) => ['queued', 'running'].includes(String(run.status)));
    if (!hasActiveRun) return undefined;
    const timer = window.setInterval(() => {
      void loadAll(true);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [runs, loadAll]);

  useEffect(() => {
    if (!selectedRunId) {
      resultRequestRef.current += 1;
      setResults(null);
      return;
    }
    const requestId = resultRequestRef.current + 1;
    resultRequestRef.current = requestId;
    retryValidationCenterRequest(() => adminAPI.getValidationRunResults(selectedRunId))
      .then((data) => {
        const summaries = asArray<ValidationSummary>(data.summaries);
        if (requestId !== resultRequestRef.current) return;
        setResults(data);
        setSelectedResultModel((previous) => {
          const resolution = resolveValidationResultResponse(
            requestId,
            resultRequestRef.current,
            previous,
            summaries,
          );
          return resolution.selectedModelId;
        });
      })
      .catch((error) => {
        if (resultRequestRef.current === requestId) {
          message.error(formatErrorMessage(error, '加载验证结果失败'));
        }
      });
  }, [selectedRunId]);

  const handleCreateDataset = async () => {
    try {
      const values = await datasetForm.validateFields();
      if (datasetImportMode === 'upload') {
        if (!datasetFile) {
          message.error('请先选择 csv、xlsx、xls、RData 或 rda 文件');
          return;
        }
        const fd = new FormData();
        fd.append('name', values.name);
        fd.append('drug_name', values.drug_name);
        fd.append('description', values.description || '');
        fd.append('file', datasetFile);
        await adminAPI.uploadValidationDataset(fd);
      } else {
        const parsed = JSON.parse(datasetJson);
        await adminAPI.createValidationDataset({
          ...values,
          subjects: parsed.subjects || [],
          dosing_events: parsed.dosing_events || [],
          observations: parsed.observations || [],
        });
      }
      message.success('验证数据集已创建');
      setDatasetModalOpen(false);
      setDatasetFile(null);
      await loadAll();
    } catch (error) {
      if (error instanceof SyntaxError) {
        message.error(`JSON 格式错误: ${error.message}`);
      } else {
        message.error(formatErrorMessage(error, '创建数据集失败'));
      }
    }
  };

  const handleDownloadDatasetTemplate = async (format: 'xlsx' | 'csv') => {
    try {
      const blob = await adminAPI.downloadValidationDatasetTemplate(format);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `precidose_validation_dataset_template.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      message.error(formatErrorMessage(error, '下载验证数据集模板失败'));
    }
  };

  const handleCreateSampleDataset = async (sampleKey: string) => {
    try {
      await adminAPI.createValidationSampleDataset(sampleKey);
      message.success('固定测试数据集已创建');
      await loadAll();
    } catch (error) {
      message.error(formatErrorMessage(error, '创建固定测试数据集失败'));
    }
  };

  const handleOpenDataset = async (datasetId: string) => {
    try {
      const detail = await adminAPI.getValidationDataset(datasetId);
      setDatasetDrawer(detail);
    } catch (error) {
      message.error(formatErrorMessage(error, '加载数据集详情失败'));
    }
  };

  const handleDeleteDataset = async (datasetId: string) => {
    try {
      await adminAPI.deleteValidationDataset(datasetId);
      message.success('验证数据集已删除');
      if (datasetDrawer?.id === datasetId) setDatasetDrawer(null);
      await loadAll();
    } catch (error) {
      message.error(formatErrorMessage(error, '删除验证数据集失败'));
    }
  };

  const handleLoadClinicalCandidates = useCallback(async (override?: Record<string, unknown>) => {
    const values = { ...clinicalForm.getFieldsValue(), ...(override || {}) };
    if (!String(values.drug_name || '').trim()) {
      setClinicalCandidates([]);
      setSelectedClinicalIds([]);
      return;
    }
    setClinicalLoading(true);
    try {
      const res = await adminAPI.listValidationClinicalCandidates({
        drug_name: values.drug_name,
        population_tag: values.population_tag,
        limit: 300,
      });
      const items = asArray<ClinicalCandidate>(res.items);
      setClinicalCandidates(items);
      setSelectedClinicalIds((current) => current.filter((id) => items.some((item) => item.calculation_id === id)));
      const duplicatesFiltered = Number(res.duplicates_filtered || 0);
      if (duplicatesFiltered > 0) {
        message.info(`已自动隐藏 ${duplicatesFiltered} 条重复临床记录`);
      }
      if (!items.length) message.info('当前筛选条件下没有可用于回顾验证的 TDM 记录');
    } catch (error) {
      message.error(formatErrorMessage(error, '加载临床候选数据失败'));
    } finally {
      setClinicalLoading(false);
    }
  }, [clinicalForm]);

  const handleOpenClinicalDatasetModal = () => {
    const today = new Date().toISOString().slice(0, 10);
    clinicalForm.setFieldsValue({
      name: `临床记录验证集 ${today}`,
      drug_name: undefined,
      population_tag: undefined,
      description: '由已提交的含 TDM 计算记录自动整理生成。',
    });
    setSelectedClinicalIds([]);
    setClinicalCandidates([]);
    setClinicalModalOpen(true);
  };

  const handleCreateClinicalDataset = async () => {
    try {
      const values = await clinicalForm.validateFields();
      if (!selectedClinicalIds.length) {
        message.warning('请先勾选至少一条临床记录');
        return;
      }
      setClinicalLoading(true);
      await adminAPI.createValidationDatasetFromClinical({
        name: values.name,
        drug_name: values.drug_name,
        description: values.description,
        population_tag: values.population_tag,
        calculation_ids: selectedClinicalIds,
      });
      message.success('临床记录验证数据集已创建');
      setClinicalModalOpen(false);
      setSelectedClinicalIds([]);
      await loadAll();
    } catch (error) {
      message.error(formatErrorMessage(error, '从临床记录创建数据集失败'));
    } finally {
      setClinicalLoading(false);
    }
  };

  const handleCreateRun = async () => {
    setRunLoading(true);
    try {
      const values = await runForm.validateFields();
      const run = await adminAPI.createValidationRun({
        dataset_id: values.dataset_id,
        model_ids: values.model_ids,
        name: values.name,
        mode: values.mode || 'population',
        settings: {
          delta: values.delta ?? 0.1,
          mc_n_sub: values.mc_n_sub ?? 0,
          mc_seed: values.mc_seed ?? 42,
        },
      });
      message.success(run.status === 'completed' ? '回顾验证已完成' : '回顾验证任务已创建');
      setRunModalOpen(false);
      setSelectedRunId(run.id);
      setActiveValidationTab('runs');
      await loadAll();
    } catch (error) {
      message.error(formatErrorMessage(error, '创建验证任务失败'));
    } finally {
      setRunLoading(false);
    }
  };

  const handleDeleteRun = async (runId: string) => {
    try {
      await adminAPI.deleteValidationRun(runId);
      message.success('验证任务历史已删除');
      if (selectedRunId === runId) {
        setSelectedRunId(undefined);
        setResults(null);
      }
      await loadAll();
    } catch (error) {
      message.error(formatErrorMessage(error, '删除验证任务失败'));
    }
  };

  const handleCancelRun = async (runId: string) => {
    try {
      await adminAPI.cancelValidationRun(runId);
      message.success('验证任务已取消');
      await loadAll();
    } catch (error) {
      message.error(formatErrorMessage(error, '取消验证任务失败'));
    }
  };

  const handleRerunRun = async (runId: string) => {
    try {
      const run = await adminAPI.rerunValidationRun(runId);
      message.success('验证任务已重新开始');
      setSelectedRunId(run.id);
      setActiveValidationTab('runs');
      await loadAll();
    } catch (error) {
      message.error(formatErrorMessage(error, '重跑验证任务失败'));
    }
  };

  const handleCopyRun = (run: ValidationRun) => {
    runForm.setFieldsValue({
      name: run.name ? `${run.name} copy` : undefined,
      dataset_id: run.dataset_id,
      model_ids: run.model_ids || [],
      delta: run.settings?.delta ?? 0.1,
      mc_n_sub: run.settings?.mc_n_sub ?? 0,
      mc_seed: run.settings?.mc_seed ?? 42,
    });
    setRunModalOpen(true);
  };

  const datasetColumns = [
    {
      title: '数据集',
      dataIndex: 'name',
      width: 200,
      render: (value: string) => <Text strong ellipsis={{ tooltip: value }}>{value}</Text>,
    },
    { title: '药物', dataIndex: 'drug_name', width: 140, render: (value: string) => <Tag className="clinical-entity-tag" style={getDrugTagStyle(value)}>{getDrugLabel(value)}</Tag> },
    {
      title: '来源',
      dataIndex: 'source_type',
      width: 110,
      render: (value?: string) => value === 'synthetic'
        ? <Tag color="gold">虚拟仿真</Tag>
        : <Tag>{value || 'manual'}</Tag>,
    },
    { title: '状态', dataIndex: 'status', width: 72, align: 'center' as const, render: (value: ValidationStatus) => statusTag(value, true, '数据集') },
    {
      title: '协变量字段',
      width: 240,
      render: (_: unknown, record: ValidationDataset) => renderTagList(record.covariate_keys, '无', 'covariate'),
    },
    {
      title: '规模',
      width: 220,
      render: (_: unknown, record: ValidationDataset) => (
        <Space>
          <Tag>{record.counts?.subjects || 0} 例</Tag>
          <Tag>{record.counts?.dosing_events || 0} 剂量</Tag>
          <Tag>{record.counts?.observations || 0} 浓度</Tag>
        </Space>
      ),
    },
    { title: '创建时间', dataIndex: 'created_at', width: 180 },
    {
      title: '操作',
      width: 180,
      render: (_: unknown, record: ValidationDataset) => (
        <Space>
          <Button icon={<DatabaseOutlined />} onClick={() => handleOpenDataset(record.id)}>详情</Button>
          <Popconfirm
            title="删除验证数据集"
            description="会同时清理该数据集下的历史验证任务和结果。"
            okText="删除"
            okButtonProps={{ danger: true }}
            cancelText="取消"
            onConfirm={() => handleDeleteDataset(record.id)}
          >
            <Button danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const clinicalCandidateColumns = [
    { title: '患者', dataIndex: 'patient_code', width: 140, render: (value: string) => <Text strong>{value}</Text> },
    { title: '药物', dataIndex: 'drug_name', width: 140, render: (value: string) => <Tag className="clinical-entity-tag" style={getDrugTagStyle(value)}>{getDrugLabel(value)}</Tag> },
    {
      title: '去重',
      width: 110,
      render: (_: unknown, record: ClinicalCandidate) => {
        const count = Number(record.duplicate_count || 1);
        return count > 1 ? <Tag color="blue">合并 {count} 条</Tag> : <Tag>唯一</Tag>;
      },
    },
    {
      title: '人群',
      dataIndex: 'population_tags',
      width: 220,
      render: (tags?: string[]) => (
        <Space wrap>
          {asArray<string>(tags).slice(0, 3).map((tag) => <Tag key={tag} className="clinical-entity-tag" color={getPopulationTagColor(tag)}>{tag}</Tag>)}
          {asArray<string>(tags).length > 3 ? <Tag>+{asArray<string>(tags).length - 3}</Tag> : null}
        </Space>
      ),
    },
    { title: 'TDM', dataIndex: 'tdm_count', width: 80 },
    { title: '剂量事件', dataIndex: 'dosing_event_count', width: 90 },
    { title: '来源模型', dataIndex: 'model_id', width: 220, render: (value?: string) => <Text code>{value || '-'}</Text> },
    { title: 'TDM 摘要', dataIndex: 'tdm_summary', width: 180 },
    { title: '提交时间', dataIndex: 'created_at', width: 180 },
  ];

  const runColumns = [
    { title: '任务', dataIndex: 'name', render: (value?: string) => <Text strong>{value || '-'}</Text> },
    { title: '状态', dataIndex: 'status', width: 72, align: 'center' as const, render: (value: ValidationStatus) => statusTag(value, true, '验证任务') },
    {
      title: '模型',
      dataIndex: 'model_ids',
      render: (items: unknown) => (
        <Space wrap>{safeArray(items).map((item) => <Tag key={item}>{item}</Tag>)}</Space>
      ),
    },
    {
      title: '进度',
      width: 180,
      render: (_: unknown, record: ValidationRun) => {
        const current = record.progress?.current ?? 0;
        const total = record.progress?.total ?? 0;
        const percent = total > 0 ? Math.round((current / total) * 100) : 0;
        return (
          <Progress
            percent={percent}
            size="small"
            status={record.status === 'failed' ? 'exception' : record.status === 'running' ? 'active' : 'normal'}
            format={() => `${current}/${total}`}
          />
        );
      },
    },
    { title: '完成时间', dataIndex: 'completed_at', width: 180 },
    {
      title: '操作',
      width: 340,
      render: (_: unknown, record: ValidationRun) => {
        const isActive = ['queued', 'running'].includes(String(record.status));
        return (
          <Space>
            <Button
              icon={<BarChartOutlined />}
              aria-label={`view-results-${record.id}`}
              onClick={() => {
                setSelectedRunId(record.id);
                setActiveValidationTab('results');
              }}
            >
              结果
            </Button>
            <Button icon={<CopyOutlined />} onClick={() => handleCopyRun(record)}>复制</Button>
            {isActive ? (
              <Popconfirm
                title="取消验证任务"
                description="正在运行的任务会在当前预测点结束后停止；已卡住的任务会被标记为失败后释放。"
                okText="取消任务"
                okButtonProps={{ danger: true }}
                cancelText="返回"
                onConfirm={() => handleCancelRun(record.id)}
              >
                <Button danger icon={<StopOutlined />}>取消</Button>
              </Popconfirm>
            ) : (
              <Button icon={<ReloadOutlined />} onClick={() => handleRerunRun(record.id)}>重跑</Button>
            )}
            <Popconfirm
              title="删除验证任务"
              description="会清理该任务的预测明细和统计汇总。运行中的任务请先取消。"
              okText="删除"
              okButtonProps={{ danger: true }}
              cancelText="取消"
              disabled={isActive}
              onConfirm={() => handleDeleteRun(record.id)}
            >
              <Button
                danger
                icon={<DeleteOutlined />}
                disabled={isActive}
              >
                删除
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const summaryColumns = [
    { title: '模型', dataIndex: 'model_id', fixed: 'left' as const, width: 220 },
    { title: '状态', dataIndex: 'overall_status', width: 72, align: 'center' as const, render: (value: ValidationStatus) => statusTag(value, true, '验证结果') },
    { title: '有效n', dataIndex: 'n_predictions', width: 80 },
    { title: '失败', dataIndex: 'failed_predictions', width: 80 },
    { title: '受试者', dataIndex: 'n_subjects', width: 80 },
    {
      title: '缺失协变量',
      dataIndex: 'missing_covariates',
      width: 180,
      render: (value?: string[]) => renderTagList(value, '-', 'covariate'),
    },
    {
      title: <Tooltip title="平均残差：observed - predicted">偏差</Tooltip>,
      dataIndex: 'mpe',
      width: 90,
      render: (value?: number) => num(value),
    },
    {
      title: <Tooltip title="平均百分预测误差：(predicted - observed) / observed × 100">MPE%</Tooltip>,
      dataIndex: 'mpe_percent',
      width: 90,
      render: (value?: number) => num(value),
    },
    { title: 'MAE', dataIndex: 'mae', width: 90, render: (value?: number) => num(value) },
    { title: 'MAPE%', dataIndex: 'mape', width: 90, render: (value?: number) => num(value) },
    { title: 'RMSE', dataIndex: 'rmse', width: 90, render: (value?: number) => num(value) },
    { title: 'F20%', dataIndex: 'f20', width: 90, render: (value?: number) => num(value, 1) },
    { title: 'F30%', dataIndex: 'f30', width: 90, render: (value?: number) => num(value, 1) },
    {
      title: <Tooltip title="仅统计存在既往 TDM、实际执行 MAP 的有效 IPRED 点">MAP成功%</Tooltip>,
      dataIndex: 'map_success_rate',
      width: 110,
      render: (value?: number) => num(value, 1),
    },
    { title: '结论', dataIndex: 'conclusion', width: 320 },
  ];

  const predictionColumns = [
    { title: '模型', dataIndex: 'model_id', width: 220 },
    { title: '类型', dataIndex: 'prediction_type', width: 90 },
    { title: '训练点', dataIndex: 'training_observation_count', width: 80 },
    { title: '观测', dataIndex: 'observed_concentration', width: 90, render: (value?: number) => num(value) },
    { title: '预测', dataIndex: 'predicted_concentration', width: 90, render: (value?: number) => num(value) },
    {
      title: <Tooltip title="observed - predicted">残差</Tooltip>,
      dataIndex: 'residual',
      width: 90,
      render: (value?: number) => num(value),
    },
    {
      title: <Tooltip title="(predicted - observed) / observed × 100">误差%</Tooltip>,
      dataIndex: 'percent_error',
      width: 90,
      render: (value?: number) => num(value),
    },
    { title: 'MAP', dataIndex: 'map_success', width: 70, align: 'center' as const, render: (value?: boolean) => <CompactStatusGlyph label="MAP" value={value ? 'success' : 'not_used'} tone={value ? 'success' : 'neutral'} /> },
    { title: '错误', dataIndex: 'error_message', width: 240 },
  ];

  const validPlotRows = selectedPredictions.filter(
    (item) => typeof item.predicted_concentration === 'number' && typeof item.observed_concentration === 'number',
  );
  const predPlotRows = validPlotRows.filter((item) => item.prediction_type === 'PRED');
  const ipredPlotRows = validPlotRows.filter((item) => item.prediction_type === 'IPRED');
  const plotRowsByType = [
    { type: 'PRED', label: 'DV-PRED', color: '#1677ff', rows: predPlotRows },
    { type: 'IPRED', label: 'DV-IPRED（MAP）', color: '#722ed1', rows: ipredPlotRows },
  ].filter((item) => item.rows.length > 0);
  const observed = validPlotRows.map((item) => item.observed_concentration);
  const predicted = validPlotRows.map((item) => Number(item.predicted_concentration));
  const identityRange = validPlotRows.length
    ? [Math.min(...predicted, ...observed), Math.max(...predicted, ...observed)]
    : [0, 1];
  const predictedRange = predicted.length ? [Math.min(...predicted), Math.max(...predicted)] : [0, 1];
  const resultSummaries = asArray<ValidationSummary>(results?.summaries);
  const validationDiagnostics = resultSummaries.map((summary) => {
    const residualBased = asRecord(asRecord(summary.residual_diagnostics).residual_based);
    return {
      summary,
      byPredictionType: asRecord(residualBased.by_prediction_type),
      notCalculated: asRecord(residualBased.not_calculated),
    };
  });
  const noValidPredictions = Boolean(results && resultSummaries.length && resultSummaries.every((summary) => !summary.n_predictions));
  const resultMissingCovariates = Array.from(new Set(resultSummaries.flatMap((summary) => asArray<string>(summary.missing_covariates))));
  const resultTopErrors = resultSummaries
    .flatMap((summary) => asArray<{ message: string; count: number }>(summary.top_errors))
    .reduce<Array<{ message: string; count: number }>>((acc, item) => {
      const existing = acc.find((row) => row.message === item.message);
      if (existing) existing.count += Number(item.count || 0);
      else acc.push({ message: item.message, count: Number(item.count || 0) });
      return acc;
    }, [])
    .sort((left, right) => right.count - left.count)
    .slice(0, 5);
  const resultModelOptions = resultSummaries.map((summary) => ({
    value: summary.model_id,
    label: summary.model_id,
  }));
  const selectedRunDataset = datasets.find((dataset) => dataset.id === selectedRunDatasetId);
  const selectedRunDatasetCovariates = asArray<string>(selectedRunDataset?.covariate_keys);
  const selectedRunDatasetDrugKey = normalizedDrugMatchKey(selectedRunDataset?.drug_name);
  const runModelOptions = models
    .filter((model) => !selectedRunDataset || normalizedDrugMatchKey(modelDrugName(model) || modelDrugLabel(model)) === selectedRunDatasetDrugKey)
    .map((model) => {
      const report = reportByModel.get(model.model_id);
      const missing = missingCovariates(report?.required_covariates, selectedRunDataset?.covariate_keys);
      const missingLabel = selectedRunDataset && missing.length ? ` · 缺 ${missing.join('/')}` : '';
      return {
        value: model.model_id,
        label: `${model.model_id} · ${modelDrugLabel(model) || '未标注'}${report ? ` · ${report.status}` : ''}${missingLabel}`,
      };
    });
  const failureDiagnosticColumns = [
    { title: '模型', dataIndex: 'model_id', width: 240, render: (value: string) => <Text code>{value}</Text> },
    { title: '尝试', dataIndex: 'attempted_predictions', width: 80 },
    { title: '失败', dataIndex: 'failed_predictions', width: 80 },
    {
      title: '缺失协变量',
      dataIndex: 'missing_covariates',
      width: 220,
      render: (value?: string[]) => renderTagList(value, '-', 'covariate'),
    },
    {
      title: '主要错误',
      dataIndex: 'top_errors',
      render: (value?: Array<{ message: string; count: number }>) => {
        const errors = asArray<{ message: string; count: number }>(value);
        if (!errors.length) return <Text type="secondary">-</Text>;
        return (
          <Space direction="vertical" size={2}>
            {errors.slice(0, 2).map((item) => (
              <Text key={item.message} type="secondary">
                {item.message} × {item.count}
              </Text>
            ))}
          </Space>
        );
      },
    },
  ];
  const datasetDetailSubjects = asArray<Record<string, unknown>>(datasetDrawer?.subjects);
  const datasetDetailDoses = asArray<Record<string, unknown>>(datasetDrawer?.dosing_events);
  const datasetDetailObservations = asArray<Record<string, unknown>>(datasetDrawer?.observations);
  const subjectCovariateKeys = collectFieldKeys(datasetDetailSubjects, 'covariates');
  const subjectStrataKeys = collectFieldKeys(datasetDetailSubjects, 'strata');
  const datasetSubjectRows = attachFlatFields(
    attachFlatFields(datasetDetailSubjects, 'covariates', 'cov_'),
    'strata',
    'strata_',
  );
  const dosingBaseKeys = new Set(['subject_key', 'event_time_h', 'time_h', 'dose_mg', 'dose', 'infusion_h', 'infusion_duration', 'interval_h', 'route']);
  const observationBaseKeys = new Set(['subject_key', 'sample_time_h', 'time_h', 'concentration', 'dv', 'unit', 'include_in_validation', 'sample_label']);
  const doseExtraKeys = collectFieldKeys(datasetDetailDoses, 'raw_payload', dosingBaseKeys);
  const observationExtraKeys = collectFieldKeys(datasetDetailObservations, 'raw_payload', observationBaseKeys);
  const datasetDoseRows = attachFlatFields(datasetDetailDoses, 'raw_payload', 'raw_', dosingBaseKeys);
  const datasetObservationRows = attachFlatFields(datasetDetailObservations, 'raw_payload', 'raw_', observationBaseKeys);
  const datasetQualityRows = buildQualityRows(datasetDrawer?.quality_report);
  const datasetQualityColumns = [
    { title: '检查项', dataIndex: 'item', width: 180 },
    { title: '结果', dataIndex: 'result', width: 160, render: formatStructuredValue },
    { title: '状态', dataIndex: 'status', width: 72, align: 'center' as const, render: (value: ValidationStatus) => statusTag(value, true, '质量检查') },
    { title: '说明', dataIndex: 'detail', render: formatStructuredValue },
  ];
  const datasetSubjectColumns = [
    { title: '受试者', dataIndex: 'subject_key', width: 160, render: (value: string) => <Text strong>{value}</Text> },
    ...dynamicFieldColumns(subjectCovariateKeys, 'cov_', '协变量'),
    ...dynamicFieldColumns(subjectStrataKeys, 'strata_', '分层'),
    { title: '备注', dataIndex: 'notes', width: 180, render: (value?: string) => value || '-' },
  ];
  const datasetDoseColumns = [
    { title: '受试者', dataIndex: 'subject_key', width: 150 },
    { title: '时间 h', dataIndex: 'event_time_h', width: 100 },
    { title: '剂量 mg', dataIndex: 'dose_mg', width: 110 },
    { title: '输注 h', dataIndex: 'infusion_h', width: 100 },
    { title: '间隔 h', dataIndex: 'interval_h', width: 100 },
    { title: '途径', dataIndex: 'route', width: 90 },
    ...dynamicFieldColumns(doseExtraKeys, 'raw_', '附加字段'),
  ];
  const datasetObservationColumns = [
    { title: '受试者', dataIndex: 'subject_key', width: 150 },
    { title: '采样 h', dataIndex: 'sample_time_h', width: 100 },
    { title: '浓度', dataIndex: 'concentration', width: 100 },
    { title: '单位', dataIndex: 'unit', width: 90 },
    {
      title: '纳入',
      dataIndex: 'include_in_validation',
      width: 80,
      render: (value?: boolean) => (value === false ? <Tag color="default">否</Tag> : <Tag color="success">是</Tag>),
    },
    { title: '标签', dataIndex: 'sample_label', width: 120, render: (value?: string) => value || '-' },
    ...dynamicFieldColumns(observationExtraKeys, 'raw_', '附加字段'),
  ];

  const completedRuns = runs.filter((run) => ['completed', 'passed'].includes(run.status)).length;
  const runningRuns = runs.filter((run) => ['running', 'queued', 'pending'].includes(run.status)).length;

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }} className="model-validation-center">
      <section className="model-validation-center__header" aria-label="临床证据中心摘要">
        <div className="model-validation-center__heading">
          <Title level={4}>临床证据中心</Title>
          <Text type="secondary">临床数据集、回顾性验证任务和结果诊断</Text>
        </div>
        <div className="model-validation-center__metrics">
          {[
            { label: '数据集', value: datasets.length },
            { label: '任务', value: runs.length },
            { label: '已完成', value: completedRuns, tone: 'success' },
            { label: '运行中', value: runningRuns, tone: 'processing' },
          ].map((item) => (
            <div key={item.label} className={`model-validation-center__metric model-validation-center__metric--${item.tone || 'default'}`}>
              <span>{item.label}</span>
              <strong>{item.value}</strong>
            </div>
          ))}
        </div>
      </section>

      <Tabs
        activeKey={activeValidationTab}
        onChange={setActiveValidationTab}
        items={[
          {
            key: 'datasets',
            label: '临床数据集',
            children: (
              <Card
                title="回顾性验证数据集"
                extra={(
                  <Space>
                    <Button icon={<UsergroupAddOutlined />} onClick={handleOpenClinicalDatasetModal}>从临床记录生成</Button>
                    <Button icon={<ExperimentOutlined />} onClick={() => setSyntheticModalOpen(true)}>生成虚拟数据</Button>
                    <Button type="primary" icon={<DatabaseOutlined />} onClick={() => setDatasetModalOpen(true)}>上传/新建数据集</Button>
                  </Space>
                )}
              >
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="验证数据集只绑定药物标签，不绑定具体模型；运行验证任务时再选择该药物下的模型进行比较。"
                />
                {sampleDatasets.length ? (
                  <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                    {sampleDatasets.map((sample) => (
                      <Col xs={24} lg={8} key={sample.key}>
                        <Card size="small" title={sample.name} extra={<Tag className="clinical-entity-tag" style={getDrugTagStyle(sample.drug_name)}>{getDrugLabel(sample.drug_name)}</Tag>}>
                          <Paragraph type="secondary" ellipsis={{ rows: 2 }}>{sample.description}</Paragraph>
                          <Space wrap>
                            <Tag>{sample.counts?.subjects || 0} 例</Tag>
                            <Tag>{sample.counts?.observations || 0} 浓度点</Tag>
                            <Button size="small" icon={<FileExcelOutlined />} onClick={() => handleCreateSampleDataset(sample.key)}>
                              创建固定测试集
                            </Button>
                          </Space>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : null}
                <Table
                  className="compact-status-pagination"
                  rowKey="id"
                  loading={loading}
                  columns={datasetColumns}
                  dataSource={datasets}
                  scroll={{ x: 1400 }}
                  pagination={compactPagination('个数据集')}
                />
              </Card>
            ),
          },
          {
            key: 'runs',
            label: <span data-testid="validation-runs-tab">回顾验证任务</span>,
            children: (
              <Card
                title="多模型回顾验证"
                extra={<Button type="primary" icon={<PlayCircleOutlined />} onClick={() => setRunModalOpen(true)}>新建验证任务</Button>}
              >
                <Table
                  className="compact-status-pagination"
                  rowKey="id"
                  loading={loading || runLoading}
                  columns={runColumns}
                  dataSource={runs}
                  scroll={{ x: 1000 }}
                  pagination={compactPagination('个验证任务')}
                />
              </Card>
            ),
          },
          {
            key: 'results',
            label: '结果分析',
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Alert
                  type="success"
                  showIcon
                  message="临床回顾验证结果会回写模型模块"
                  description="仅非 synthetic 的临床回顾验证会把最新外部验证摘要同步到模型详情并更新临床证据状态；虚拟数据集只保留技术诊断，不会形成外部临床验证结论。临床证据中心保留逐点预测和诊断图。"
                />
                <Card>
                  <Space wrap>
                    <Text strong>选择验证任务</Text>
                    <Select
                      style={{ minWidth: 320 }}
                      value={selectedRunId}
                      onChange={setSelectedRunId}
                      options={runs.map((run) => ({ value: run.id, label: run.name || run.id }))}
                    />
                    <Text strong>诊断模型</Text>
                    <Select
                      style={{ minWidth: 260 }}
                      value={selectedResultModel}
                      onChange={setSelectedResultModel}
                      options={resultModelOptions}
                    />
                  </Space>
                </Card>
                {!results ? (
                  <Card><Empty description="请选择一个已完成的验证任务" /></Card>
                ) : (
                  <>
                    {noValidPredictions ? (
                      <Alert
                        type="warning"
                        showIcon
                        message="任务已完成，但没有生成有效预测"
                        description={(
                          <Space direction="vertical" size={4}>
                            <Text>
                              这通常表示所选模型需要的协变量在当前数据集中缺失，或脚本编译/运行失败。
                              {resultMissingCovariates.length ? ` 当前聚合缺失：${resultMissingCovariates.join('、')}。` : ''}
                            </Text>
                            {resultTopErrors.length ? (
                              <Text type="secondary">
                                主要错误：{resultTopErrors.map((item) => `${item.message} × ${item.count}`).join('；')}
                              </Text>
                            ) : null}
                          </Space>
                        )}
                      />
                    ) : null}
                    <Table
                      rowKey="id"
                      columns={summaryColumns}
                      dataSource={resultSummaries}
                      scroll={{ x: 1550 }}
                      pagination={false}
                    />
                    <Card title="预测类型与残差诊断完整性">
                      <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        {validationDiagnostics.map(({ summary, byPredictionType, notCalculated }) => {
                          const pred = asRecord(byPredictionType.PRED);
                          const ipred = asRecord(byPredictionType.IPRED);
                          return (
                            <Descriptions key={summary.id} size="small" bordered column={3}>
                              <Descriptions.Item label="模型">{summary.model_id}</Descriptions.Item>
                              <Descriptions.Item label="正式验证指标">PRED（群体预测）</Descriptions.Item>
                              <Descriptions.Item label="MAP 成功率">
                                {summary.map_success_rate == null ? '-' : `${num(summary.map_success_rate, 1)}%`}
                              </Descriptions.Item>
                              <Descriptions.Item label="PRED：n / MAPE">
                                {String(pred.n ?? 0)} / {pred.mape == null ? '-' : `${num(Number(pred.mape))}%`}
                              </Descriptions.Item>
                              <Descriptions.Item label="IPRED：n / MAPE">
                                {String(ipred.n ?? 0)} / {ipred.mape == null ? '-' : `${num(Number(ipred.mape))}%`}
                              </Descriptions.Item>
                              <Descriptions.Item label="CWRES / IWRES / NPDE" span={3}>
                                {Object.keys(notCalculated).length
                                  ? `CWRES：${String(notCalculated.cwres || '未报告')}；IWRES：${String(notCalculated.iwres || '未报告')}；NPDE：${String(notCalculated.npde || '未报告')}`
                                  : '未报告'}
                              </Descriptions.Item>
                            </Descriptions>
                          );
                        })}
                      </Space>
                    </Card>
                    {noValidPredictions || resultSummaries.some((summary) => asArray(summary.top_errors).length) ? (
                      <Card title="失败诊断">
                        <Table
                          rowKey="id"
                          columns={failureDiagnosticColumns}
                          dataSource={resultSummaries}
                          size="small"
                          pagination={false}
                          scroll={{ x: 1000 }}
                        />
                      </Card>
                    ) : null}
                    {validPlotRows.length ? (
                      <Row gutter={[16, 16]}>
                        <Col xs={24} xl={12}>
                          <Card title="观测值 vs 预测值">
                            <Plot
                              data={[
                                ...plotRowsByType.map((series) => ({
                                  x: series.rows.map((item) => item.predicted_concentration),
                                  y: series.rows.map((item) => item.observed_concentration),
                                  mode: 'markers',
                                  type: 'scatter',
                                  marker: { color: series.color, size: 8 },
                                  name: series.label,
                                })),
                                {
                                  x: identityRange,
                                  y: identityRange,
                                  mode: 'lines',
                                  type: 'scatter',
                                  line: { color: '#8c8c8c', dash: 'dash' },
                                  name: 'identity',
                                },
                              ]}
                              layout={{ autosize: true, height: 320, margin: { l: 46, r: 16, t: 12, b: 44 }, xaxis: { title: 'Predicted' }, yaxis: { title: 'Observed' } }}
                              style={{ width: '100%' }}
                              config={{ displayModeBar: false, responsive: true }}
                            />
                          </Card>
                        </Col>
                        <Col xs={24} xl={12}>
                          <Card title="残差 vs 预测值">
                            <Plot
                              data={[
                                ...plotRowsByType.map((series) => ({
                                  x: series.rows.map((item) => item.predicted_concentration),
                                  y: series.rows.map((item) => item.residual ?? 0),
                                  mode: 'markers',
                                  type: 'scatter',
                                  marker: { color: series.color, size: 8 },
                                  name: `${series.type} residual`,
                                })),
                                {
                                  x: predictedRange,
                                  y: [0, 0],
                                  mode: 'lines',
                                  type: 'scatter',
                                  line: { color: '#8c8c8c', dash: 'dash' },
                                  name: 'zero',
                                },
                              ]}
                              layout={{ autosize: true, height: 320, margin: { l: 46, r: 16, t: 12, b: 44 }, xaxis: { title: 'Predicted' }, yaxis: { title: 'Residual' } }}
                              style={{ width: '100%' }}
                              config={{ displayModeBar: false, responsive: true }}
                            />
                          </Card>
                        </Col>
                        <Col xs={24} xl={12}>
                          <Card title="百分误差分布">
                            <Plot
                              data={plotRowsByType.map((series) => ({
                                x: series.rows
                                  .map((item) => item.percent_error)
                                  .filter((item): item is number => typeof item === 'number' && Number.isFinite(item)),
                                type: 'histogram',
                                opacity: 0.65,
                                marker: { color: series.color },
                                name: `${series.type} PE%`,
                              }))}
                              layout={{ autosize: true, height: 300, margin: { l: 46, r: 16, t: 12, b: 44 }, xaxis: { title: 'Percent error (%)' }, yaxis: { title: 'Count' } }}
                              style={{ width: '100%' }}
                              config={{ displayModeBar: false, responsive: true }}
                            />
                          </Card>
                        </Col>
                        <Col xs={24} xl={12}>
                          <Card title="模拟诊断概览">
                            {resultSummaries.map((summary) => (
                              <Descriptions key={summary.id} column={1} size="small" bordered style={{ marginBottom: 12 }}>
                                <Descriptions.Item label="模型">{summary.model_id}</Descriptions.Item>
                                <Descriptions.Item label="模拟区间">
                                  {(summary.simulation_diagnostics as Record<string, unknown>)?.available ? '已生成' : '未启用'}
                                </Descriptions.Item>
                                <Descriptions.Item label="区间覆盖">
                                  {JSON.stringify((summary.simulation_diagnostics as Record<string, unknown>)?.prediction_interval_coverage || {})}
                                </Descriptions.Item>
                              </Descriptions>
                            ))}
                          </Card>
                        </Col>
                      </Row>
                    ) : (
                      <Card>
                        <Empty description="暂无有效预测点，诊断图无法生成。请查看失败诊断和逐点错误。" />
                      </Card>
                    )}
                    <Card title="逐点预测明细">
                      <Table
                        rowKey="id"
                        columns={predictionColumns}
                        dataSource={selectedPredictions}
                        scroll={{ x: 1200 }}
                        size="small"
                      />
                    </Card>
                  </>
                )}
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title="新建验证数据集"
        open={datasetModalOpen}
        onCancel={() => setDatasetModalOpen(false)}
        onOk={handleCreateDataset}
        width={860}
        okText="创建"
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="数据集只需要药物标签，不绑定模型。支持 csv、xlsx、xls、RData、rda 上传；也可用 JSON 兜底导入。"
        />
        <Form form={datasetForm} layout="vertical">
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="name" label="数据集名称" rules={[{ required: true }]}>
                <Input placeholder="如：2025 万古霉素 PICU 外部验证集" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="drug_name" label="药物" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={drugOptions}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="说明">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Radio.Group
            value={datasetImportMode}
            onChange={(event) => setDatasetImportMode(event.target.value)}
            style={{ marginBottom: 12 }}
          >
            <Radio.Button value="upload">文件上传</Radio.Button>
            <Radio.Button value="json">JSON 导入</Radio.Button>
          </Radio.Group>
          {datasetImportMode === 'upload' ? (
            <Form.Item label="临床验证文件">
              <Space wrap style={{ marginBottom: 8 }}>
                <Button icon={<DownloadOutlined />} onClick={() => handleDownloadDatasetTemplate('xlsx')}>
                  下载 Excel 模板
                </Button>
                <Button icon={<DownloadOutlined />} onClick={() => handleDownloadDatasetTemplate('csv')}>
                  下载 CSV 模板
                </Button>
              </Space>
              <Upload
                accept=".csv,.xlsx,.xls,.RData,.rdata,.rda"
                beforeUpload={(file) => {
                  setDatasetFile(file);
                  return false;
                }}
                maxCount={1}
                onRemove={() => setDatasetFile(null)}
              >
                <Button icon={<UploadOutlined />}>选择文件</Button>
              </Upload>
              <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                多工作表建议命名为 subjects、dosing_events、observations；单表长表需包含 subject_key、time/sample_time_h、dose_mg、concentration 等字段。
              </Paragraph>
            </Form.Item>
          ) : (
            <Form.Item label="临床验证 JSON">
              <Input.TextArea
                rows={16}
                value={datasetJson}
                onChange={(event) => setDatasetJson(event.target.value)}
                spellCheck={false}
                style={{ fontFamily: 'Consolas, monospace' }}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <SyntheticValidationDatasetModal
        open={syntheticModalOpen}
        models={models}
        onCancel={() => setSyntheticModalOpen(false)}
        onCreated={() => loadAll()}
      />

      <Modal
        title="从临床记录生成验证数据集"
        open={clinicalModalOpen}
        onCancel={() => setClinicalModalOpen(false)}
        onOk={handleCreateClinicalDataset}
        confirmLoading={clinicalLoading}
        width={1040}
        okText="生成数据集"
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="系统会从已提交的含 TDM 计算记录中提取协变量、给药事件和观测浓度；数据集仍只绑定药物标签，不绑定具体模型。"
        />
        <Form form={clinicalForm} layout="vertical">
          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item name="name" label="数据集名称" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="drug_name" label="药物" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={drugOptions}
                  onChange={() => {
                    setSelectedClinicalIds([]);
                    void handleLoadClinicalCandidates();
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="population_tag" label="人群">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={populationTagOptions}
                  onChange={() => {
                    setSelectedClinicalIds([]);
                    void handleLoadClinicalCandidates();
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item label=" ">
                <Button block icon={<ReloadOutlined />} loading={clinicalLoading} onClick={() => handleLoadClinicalCandidates()}>
                  刷新候选
                </Button>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="说明">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
        <Table
          rowKey="calculation_id"
          size="small"
          loading={clinicalLoading}
          columns={clinicalCandidateColumns}
          dataSource={clinicalCandidates}
          rowSelection={{
            selectedRowKeys: selectedClinicalIds,
            onChange: (keys) => setSelectedClinicalIds(keys.map(String)),
          }}
          scroll={{ x: 1250, y: 360 }}
          pagination={{ pageSize: 8 }}
        />
      </Modal>

      <Modal
        title="新建回顾验证任务"
        open={runModalOpen}
        onCancel={() => setRunModalOpen(false)}
        onOk={handleCreateRun}
        confirmLoading={runLoading}
        width={720}
        okText="运行"
      >
        <Form form={runForm} layout="vertical" initialValues={{ mode: 'population', delta: 0.1, mc_n_sub: 0, mc_seed: 42 }}>
          <Form.Item name="name" label="任务名称">
            <Input placeholder="可选" />
          </Form.Item>
          <Form.Item name="dataset_id" label="验证数据集" rules={[{ required: true }]}>
            <Select
              onChange={(datasetId) => {
                const dataset = datasets.find((item) => item.id === datasetId);
                const drugKey = normalizedDrugMatchKey(dataset?.drug_name);
                const eligible = new Set(
                  models
                    .filter((model) => !dataset || normalizedDrugMatchKey(modelDrugName(model) || modelDrugLabel(model)) === drugKey)
                    .map((model) => model.model_id),
                );
                const selected = asArray<string>(runForm.getFieldValue('model_ids'));
                runForm.setFieldsValue({ model_ids: selected.filter((modelId) => eligible.has(modelId)) });
              }}
              options={datasets.map((dataset) => ({
                value: dataset.id,
                label: `${dataset.name} · ${getDrugLabel(dataset.drug_name)}`,
              }))}
            />
          </Form.Item>
          {selectedRunDataset ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={`当前数据集药物标签：${getDrugLabel(selectedRunDataset.drug_name)}。下方仅显示同药物标签模型。`}
              description={selectedRunDatasetCovariates.length ? `数据集可用协变量：${selectedRunDatasetCovariates.join('、')}` : '该数据集未识别到受试者协变量，运行后可能无法生成有效预测。'}
            />
          ) : null}
          <Form.Item name="model_ids" label="比较模型" rules={[{ required: true }]}>
            <Select
              mode="multiple"
              optionFilterProp="label"
              options={runModelOptions}
              notFoundContent={selectedRunDataset ? '没有同药物标签模型，请先在脚本健康页标注模型药物' : '请先选择数据集'}
            />
          </Form.Item>
          <Form.Item
            name="mode"
            label="预测模式"
            tooltip="群体 PRED 是正式外部验证；顺序 MAP 仅评价使用既往 TDM 后的贝叶斯前瞻预测。"
          >
            <Select
              options={[
                { value: 'population', label: '群体 PRED（外部验证，不使用任何 TDM）' },
                { value: 'sequential_map', label: '顺序 MAP（贝叶斯前瞻预测诊断）' },
              ]}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="delta" label="模拟步长 h">
                <InputNumber min={0.01} max={1} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="mc_n_sub" label="模拟诊断 N">
                <InputNumber min={0} max={1000} step={10} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="mc_seed" label="随机种子">
                <InputNumber min={1} max={999999} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Paragraph type="secondary">
            群体 PRED 的 F20/F30、MAPE 用于外部验证筛查。顺序 MAP 时，模型库状态仍只依据 PRED；IPRED 会单独保留为 MAP 成功率和贝叶斯预测诊断。N ≥ 5 时额外计算基于模拟的预测区间覆盖。
          </Paragraph>
        </Form>
      </Modal>

      <Modal
        title="验证数据集详情"
        open={Boolean(datasetDrawer)}
        width={1180}
        footer={null}
        onCancel={() => setDatasetDrawer(null)}
        destroyOnHidden
      >
        {datasetDrawer ? (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="名称">{String(datasetDrawer.name || '-')}</Descriptions.Item>
              <Descriptions.Item label="药物">{getDrugLabel(String(datasetDrawer.drug_name || ''))}</Descriptions.Item>
              <Descriptions.Item label="状态">{statusTag(String(datasetDrawer.status || ''))}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{String(datasetDrawer.created_at || '-')}</Descriptions.Item>
            </Descriptions>
            <Card
              size="small"
              title="质量检查"
              extra={statusTag(String((datasetDrawer.quality_report as Record<string, string>)?.status || datasetDrawer.status || 'warning'))}
            >
              <Table
                rowKey={(record) => String(record.id)}
                columns={datasetQualityColumns}
                dataSource={datasetQualityRows}
                size="small"
                pagination={false}
              />
            </Card>
            <Tabs
              items={[
                {
                  key: 'subjects',
                  label: `受试者 (${datasetDetailSubjects.length})`,
                  children: (
                    <Table
                      rowKey={(record) => String(record.id || record.subject_key)}
                      columns={datasetSubjectColumns}
                      dataSource={datasetSubjectRows}
                      size="small"
                      scroll={{ x: Math.max(900, 220 + (subjectCovariateKeys.length + subjectStrataKeys.length) * 140), y: 360 }}
                      pagination={{ pageSize: 8 }}
                    />
                  ),
                },
                {
                  key: 'doses',
                  label: `给药事件 (${datasetDetailDoses.length})`,
                  children: (
                    <Table
                      rowKey={(record) => String(record.id || `${record.subject_key}-${record.event_time_h}`)}
                      columns={datasetDoseColumns}
                      dataSource={datasetDoseRows}
                      size="small"
                      scroll={{ x: Math.max(950, 650 + doseExtraKeys.length * 140), y: 360 }}
                      pagination={{ pageSize: 8 }}
                    />
                  ),
                },
                {
                  key: 'observations',
                  label: `观测浓度 (${datasetDetailObservations.length})`,
                  children: (
                    <Table
                      rowKey={(record) => String(record.id || `${record.subject_key}-${record.sample_time_h}`)}
                      columns={datasetObservationColumns}
                      dataSource={datasetObservationRows}
                      size="small"
                      scroll={{ x: Math.max(1000, 720 + observationExtraKeys.length * 140), y: 360 }}
                      pagination={{ pageSize: 8 }}
                    />
                  ),
                },
              ]}
            />
          </Space>
        ) : null}
      </Modal>
    </Space>
  );
};

export default ModelValidationCenter;
